import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const knowledgeLibraryConfigurationValueSchema = z.strictObject({
    maximumDocuments: z.number().int().min(1).max(10_000),
    maximumVersionsPerDocument: z.number().int().min(1).max(1_000),
    defaultReviewCycleDays: z.number().int().min(1).max(3_650).nullable(),
    defaultRequiresAcknowledgement: z.boolean(),
});
export const knowledgeLibraryConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'knowledge.library.configuration', moduleId: 'knowledge', title: 'Knowledge library behavior',
        helpText: 'Controls tenant library limits and new-document review and acknowledgement defaults.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            maximumDocuments: 1_000, maximumVersionsPerDocument: 250,
            defaultReviewCycleDays: 365, defaultRequiresAcknowledgement: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: knowledgeLibraryConfigurationValueSchema,
});
export const knowledgeLibraryDisplayValueSchema = z.strictObject({
    layout: z.enum(['grid', 'list']), cardSize: z.enum(['compact', 'standard', 'large']),
    density: z.enum(['comfortable', 'compact']), showSummary: z.boolean(),
    showDocumentType: z.boolean(), showVersion: z.boolean(),
});
export const knowledgeLibraryDisplaySetting = definePlatformSetting({
    definition: {
        key: 'knowledge.display.library', moduleId: 'knowledge', title: 'Knowledge library display',
        helpText: 'Controls library layout, card size, density, and visible document metadata.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            layout: 'grid', cardSize: 'standard', density: 'comfortable',
            showSummary: true, showDocumentType: true, showVersion: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: knowledgeLibraryDisplayValueSchema,
});
export const knowledgeGovernanceConfigurationValueSchema = z.strictObject({
    maximumCollectionDepth: z.number().int().min(1).max(20),
    maximumAccessRulesPerTarget: z.number().int().min(1).max(250),
    defaultReviewDueDays: z.number().int().min(1).max(365).nullable(),
});
export const knowledgeGovernanceConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'knowledge.governance.configuration', moduleId: 'knowledge', title: 'Knowledge governance behavior',
        helpText: 'Controls collection nesting, access-rule limits, and the default review due date.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: { maximumCollectionDepth: 8, maximumAccessRulesPerTarget: 100, defaultReviewDueDays: 14 },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: knowledgeGovernanceConfigurationValueSchema,
});
export const knowledgeGovernanceDisplayValueSchema = z.strictObject({
    collectionLayout: z.enum(['tree', 'cards']), reviewDensity: z.enum(['comfortable', 'compact']),
    showCompletedReviews: z.boolean(), groupReviewsByStatus: z.boolean(),
});
export const knowledgeGovernanceDisplaySetting = definePlatformSetting({
    definition: {
        key: 'knowledge.display.governance', moduleId: 'knowledge', title: 'Knowledge governance display',
        helpText: 'Controls collection and review layout without changing access decisions.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: { collectionLayout: 'tree', reviewDensity: 'comfortable', showCompletedReviews: true, groupReviewsByStatus: true },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: knowledgeGovernanceDisplayValueSchema,
});
const statusLabelsSchema = z.strictObject({
    draft: z.string().trim().min(1).max(60), published: z.string().trim().min(1).max(60),
    archived: z.string().trim().min(1).max(60), pending: z.string().trim().min(1).max(60),
    acknowledged: z.string().trim().min(1).max(60),
});
export const knowledgeLibraryCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    managementNavigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(120), description: z.string().trim().min(1).max(300),
    managementTitle: z.string().trim().min(1).max(120),
    managementDescription: z.string().trim().min(1).max(300),
    searchLabel: z.string().trim().min(1).max(80), openAction: z.string().trim().min(1).max(80),
    acknowledgeAction: z.string().trim().min(1).max(80), acknowledgementLabel: z.string().trim().min(1).max(120),
    acknowledgementText: z.string().trim().min(1).max(500),
    createAction: z.string().trim().min(1).max(80), saveVersionAction: z.string().trim().min(1).max(80),
    publishAction: z.string().trim().min(1).max(80), archiveAction: z.string().trim().min(1).max(80),
    rollbackAction: z.string().trim().min(1).max(80), titleLabel: z.string().trim().min(1).max(80),
    slugLabel: z.string().trim().min(1).max(80), summaryLabel: z.string().trim().min(1).max(80),
    typeLabel: z.string().trim().min(1).max(80), bodyLabel: z.string().trim().min(1).max(80),
    reasonLabel: z.string().trim().min(1).max(80), requiresAcknowledgementLabel: z.string().trim().min(1).max(120),
    signedPolicyLabel: z.string().trim().min(1).max(120),
    signedPolicyPageCountLabel: z.string().trim().min(1).max(120),
    signedPolicyConsentLabel: z.string().trim().min(1).max(120),
    loadingLabel: z.string().trim().min(1).max(120), emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300), loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240), savedLabel: z.string().trim().min(1).max(120),
    versionLabel: z.string().trim().min(1).max(80), statusLabels: statusLabelsSchema,
});
export const knowledgeLibraryCopySetting = definePlatformSetting({
    definition: {
        key: 'knowledge.copy.library', moduleId: 'knowledge', title: 'Knowledge library copy',
        helpText: 'Controls tenant-editable library, acknowledgement, and authoring labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Company Library', managementNavigationLabel: 'Knowledge management',
            title: 'Company Knowledge', description: 'Published policies, SOPs, safety guidance, and reference material.',
            managementTitle: 'Knowledge management', managementDescription: 'Create immutable versions and control publication without overwriting history.',
            searchLabel: 'Search the library', openAction: 'Open document', acknowledgeAction: 'Acknowledge this version',
            acknowledgementLabel: 'Version acknowledgement',
            acknowledgementText: 'I have read and acknowledge this exact published version.',
            createAction: 'Create document', saveVersionAction: 'Save new version', publishAction: 'Publish version',
            archiveAction: 'Archive document', rollbackAction: 'Publish historical version as new version',
            titleLabel: 'Title', slugLabel: 'Slug', summaryLabel: 'Summary', typeLabel: 'Document type',
            bodyLabel: 'Markdown content', reasonLabel: 'Change reason',
            requiresAcknowledgementLabel: 'Require acknowledgement for this version',
            signedPolicyLabel: 'Require controlled page initials and signature for this policy',
            signedPolicyPageCountLabel: 'Published policy page count',
            signedPolicyConsentLabel: 'Electronic signature consent statement',
            loadingLabel: 'Loading Knowledge…', emptyTitle: 'No published Knowledge documents',
            emptyDescription: 'Published policies, SOPs, and reference material will appear here.',
            loadError: 'Knowledge could not be loaded.', mutationError: 'The Knowledge change could not be saved.',
            savedLabel: 'Knowledge saved.', versionLabel: 'Version',
            statusLabels: { draft: 'Draft', published: 'Published', archived: 'Archived', pending: 'Pending', acknowledged: 'Acknowledged' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: knowledgeLibraryCopyValueSchema,
});
const governanceReviewLabelsSchema = z.strictObject({
    pending: z.string().trim().min(1).max(60), changes_requested: z.string().trim().min(1).max(60),
    approved: z.string().trim().min(1).max(60), skipped: z.string().trim().min(1).max(60),
});
export const knowledgeGovernanceCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60), title: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(300), collectionsTitle: z.string().trim().min(1).max(120),
    reviewsTitle: z.string().trim().min(1).max(120), createCollectionAction: z.string().trim().min(1).max(80),
    updateCollectionAction: z.string().trim().min(1).max(80), archiveCollectionAction: z.string().trim().min(1).max(80),
    saveAccessAction: z.string().trim().min(1).max(80), assignReviewAction: z.string().trim().min(1).max(80),
    approveAction: z.string().trim().min(1).max(80), requestChangesAction: z.string().trim().min(1).max(80),
    skipAction: z.string().trim().min(1).max(80), noCollectionsLabel: z.string().trim().min(1).max(160),
    noReviewsLabel: z.string().trim().min(1).max(160), loadingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240), mutationError: z.string().trim().min(1).max(240),
    savedLabel: z.string().trim().min(1).max(120), reviewStatusLabels: governanceReviewLabelsSchema,
});
export const knowledgeGovernanceCopySetting = definePlatformSetting({
    definition: {
        key: 'knowledge.copy.governance', moduleId: 'knowledge', title: 'Knowledge governance copy',
        helpText: 'Controls tenant-editable collection, access, and review labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Knowledge governance', title: 'Knowledge governance',
            description: 'Organize access-controlled collections and complete version-specific reviews.',
            collectionsTitle: 'Collections', reviewsTitle: 'Reviews', createCollectionAction: 'Create collection',
            updateCollectionAction: 'Update collection', archiveCollectionAction: 'Archive collection',
            saveAccessAction: 'Save document access', assignReviewAction: 'Assign review', approveAction: 'Approve',
            requestChangesAction: 'Request changes', skipAction: 'Skip review', noCollectionsLabel: 'No collections yet.',
            noReviewsLabel: 'No reviews match this view.', loadingLabel: 'Loading Knowledge governance…',
            loadError: 'Knowledge governance could not be loaded.', mutationError: 'The governance change could not be saved.',
            savedLabel: 'Knowledge governance saved.',
            reviewStatusLabels: { pending: 'Pending', changes_requested: 'Changes requested', approved: 'Approved', skipped: 'Skipped' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: knowledgeGovernanceCopyValueSchema,
});
export const knowledgeSettingRegistrations = [
    knowledgeLibraryConfigurationSetting, knowledgeLibraryDisplaySetting, knowledgeLibraryCopySetting,
    knowledgeGovernanceConfigurationSetting, knowledgeGovernanceDisplaySetting, knowledgeGovernanceCopySetting,
];
//# sourceMappingURL=knowledge-setting-definitions.js.map